import { useState, useEffect, type RefObject } from 'react';

// Rounds `rect.top + window.scrollY` to an integer. On iOS Safari, momentum
// scrolling and address-bar show/hide cause sub-pixel jitter between those
// two values, so without rounding we produce a different scrollBase on every
// intersection callback. That flows into pro-gallery's significant-props
// comparison (which uses JSON.stringify) and forces a full item remount on
// every scroll tick, detaching gallery-item-container nodes mid-tap.
const measure = (el: HTMLElement): number =>
  Math.round(el.getBoundingClientRect().top + window.scrollY);

export const useScrollBase = (containerRef: RefObject<HTMLElement>): number => {
  const [scrollBase, setScrollBase] = useState(0);
  useEffect(() => {
    let observedContainer: HTMLElement | null = null;
    let rafId: number | null = null;

    const updateScrollBase = (el: HTMLElement) => {
      const next = measure(el);
      setScrollBase((prev) => (prev === next ? prev : next));
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || !observedContainer) {
        return;
      }
      // Defer to rAF so we sample after scroll position and layout settle,
      // not mid-frame where rect.top and window.scrollY disagree on iOS.
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (observedContainer) {
          updateScrollBase(observedContainer);
        }
      });
    });

    if (containerRef.current) {
      observedContainer = containerRef.current;
      updateScrollBase(observedContainer);
      observer.observe(observedContainer);
    }
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (observedContainer) {
        observer.unobserve(observedContainer);
      }
    };
  }, [containerRef]);

  return scrollBase;
};
